import { useEffect, useState } from 'react';
import axios from 'axios';
import dbUrl from '../../utils/credentials';
import InvitationPreview from '../../components/invitation/InvitationPreview';
import InvitationCanvas from '../../components/invitation/InvitationCanvas';
import {
  getInitialFieldValues,
  getVisibleLeafFields,
  isInvitationOpen,
  normalizeInvitation,
  pruneHiddenFieldValues,
  validateVisibleFields,
  descriptionToPlainText,
} from '../../lib/invitation';
import { submitResponse } from '../../lib/responses';
import { toast } from '../../lib/toast';
import ErrorPage from './error/index';
import { Card, CardContent } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';

function sanitizeFirebaseKey(rawKey) {
  if (!rawKey) return '';
  return rawKey
    .toString()
    .replace(/[.#$/[\]]/g, '')
    .trim()
    .replace(/\s+/g, '_');
}

function Invitations() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [invitationId, setInvitationId] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    if (!id) {
      setError('ID invito mancante nell\'URL');
      setLoading(false);
      return;
    }
    setInvitationId(id);
    fetchInvitation(id);
  }, []);

  useEffect(() => {
    if (!invitation) return;
    document.title = invitation.name || 'Invito';
    const metaDescription = document.querySelector('meta[name="description"]');
    const content = descriptionToPlainText(invitation.description || '');
    if (metaDescription) {
      metaDescription.setAttribute('content', content);
    } else {
      const descTag = document.createElement('meta');
      descTag.name = 'description';
      descTag.content = content;
      document.head.appendChild(descTag);
    }
  }, [invitation]);

  const fetchInvitation = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${dbUrl}invitations/${id}.json`);
      if (!res.data) {
        setError('Questo invito non esiste o è stato eliminato. Contatta l\'organizzatore per maggiori informazioni.');
      } else {
        const normalized = normalizeInvitation(res.data);
        setInvitation(normalized);
        setFormValues(getInitialFieldValues(normalized.fields));
      }
    } catch (e) {
      console.error(e);
      setError('Errore durante il caricamento dell\'invito');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormValues((prev) => {
      const next = { ...prev, [field]: value };
      return invitation ? pruneHiddenFieldValues(next, invitation.fields) : next;
    });
  };

  const handleCheckboxChange = (field, optionValue, isChecked) => {
    setFormValues((prev) => {
      const current = Array.isArray(prev[field]) ? prev[field] : [];
      const nextValues = isChecked
        ? [...current.filter((v) => v !== optionValue), optionValue]
        : current.filter((v) => v !== optionValue);
      const next = { ...prev, [field]: nextValues };
      return invitation ? pruneHiddenFieldValues(next, invitation.fields) : next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!invitation || !invitationId) {
      setError('Dati invito mancanti');
      return;
    }
    if (!isInvitationOpen(invitation)) {
      setError('Le iscrizioni per questo invito sono chiuse.');
      return;
    }

    const invalidFields = validateVisibleFields(invitation.fields, formValues);
    if (invalidFields.length > 0) {
      setError(`Compila correttamente il campo "${invalidFields[0]}".`);
      return;
    }

    const answers = getVisibleLeafFields(invitation.fields, formValues).reduce((acc, field, index) => {
      const label = field.name;
      const value = formValues[label];
      const safeKey = sanitizeFirebaseKey(label);
      const key = safeKey || `field_${index + 1}`;
      acc[key] = { label, value };
      return acc;
    }, {});

    setSubmitting(true);
    try {
      await submitResponse(invitationId, answers);
      setSubmitted(true);
      toast.success('Iscrizione inviata', 'Grazie per la tua partecipazione!');
    } catch {
      setError('Errore durante l\'invio della partecipazione');
      toast.error('Invio non riuscito', 'Riprova tra qualche istante.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <InvitationCanvas fullPage>
        <div className="mx-auto max-w-2xl px-4 py-8 md:py-12">
          <Card>
            <CardContent className="space-y-6 p-6">
              <Skeleton className="mx-auto h-24 w-24 rounded-full" />
              <Skeleton className="mx-auto h-8 w-2/3" />
              <Skeleton className="mx-auto h-4 w-full" />
              <Skeleton className="mx-auto h-4 w-4/5" />
              <div className="space-y-4 pt-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ))}
              </div>
              <Skeleton className="h-11 w-full rounded-lg" />
            </CardContent>
          </Card>
        </div>
      </InvitationCanvas>
    );
  }
  if (error) return <ErrorPage error={error} />;
  if (!invitation) return null;

  const open = isInvitationOpen(invitation);

  return (
    <InvitationCanvas invitation={invitation} fullPage>
      <div className="mx-auto max-w-2xl px-4 py-8 md:py-12">
        <InvitationPreview
          invitation={invitation}
          mode={open && !submitted ? 'interactive' : 'preview'}
          formValues={formValues}
          onFieldChange={handleChange}
          onCheckboxChange={handleCheckboxChange}
          onSubmit={handleSubmit}
          submitted={submitted}
          submittedSummary={submitted ? formValues : null}
          closed={!open && !submitted}
        />
        {submitting && (
          <p className="mt-4 text-center text-sm ba-muted animate-pulse">Invio in corso…</p>
        )}
      </div>
    </InvitationCanvas>
  );
}

export default Invitations;
